import { useEffect, useMemo, useRef, useState } from 'react';

import {
    Box,
    Button,
    Card,
    Collapse,
    Divider,
    Flex,
    Heading,
    Image,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Text,
    useBreakpointValue
} from '@chakra-ui/react';

import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";

function ShirtInput({size, label, quant, changeFunc}) {
    return (
        <Box>
            <Flex flexDirection="row">
                <Card align="center" width={150} padding={1} mr={2} bgColor="whiteAlpha.300">{label}</Card>
                <NumberInput precision={0} size='sm' maxW={20} value={quant} min={0} max={15} onChange={(newQuant)=>changeFunc(size, newQuant)}>
                    <NumberInputField />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                </NumberInput>
            </Flex>
        </Box>
    );
}

export function ShirtForm({eventConfig, requestActive, buyShirts, recordShirts}) {

    const shirtDefaults = {
        "msm": {"label": "Men's Small", "quant": 0},
        "mmd": {"label": "Men's Medium", "quant": 0},
        "mlg": {"label": "Men's Large", "quant": 0},
        "mxl": {"label": "Men's XL", "quant": 0},
        "m2xl": {"label": "Men's 2XL", "quant": 0},
        "wsm": {"label": "Women's Small", "quant": 0},
        "wmd": {"label": "Women's Medium", "quant": 0},
        "wlg": {"label": "Women's Large", "quant": 0},
        "wxl": {"label": "Women's XL", "quant": 0},
        "w2xl": {"label": "Women's 2XL", "quant": 0}
    };

    const [shirtQuants, setShirtQuants] = useState(shirtDefaults);
    const [totalCost, setTotalCost] = useState(0);

    const changeQuant = (size, quant) => {
        const newShirtQuants = shirtQuants;

        newShirtQuants[size].quant = quant;

        let newTotalCost = 0;

        Object.values(newShirtQuants).forEach((size)=>{
            const nextQuant = parseInt(size.quant);
            newTotalCost += nextQuant;
        })

        newTotalCost *= eventConfig["shirtsPrice"];

        setShirtQuants({...newShirtQuants});
        setTotalCost(newTotalCost);
    };

    const isMobile = useBreakpointValue({
        "base": ()=> true,
        "xl": ()=>  false
    });
    
    const style = {"layout":"vertical"};

    const menShirtInputDisplay = Object.keys(shirtQuants)?.map((size, idx)=> {
        if (shirtQuants[size].label.includes("Men")) {
            return (
                <ShirtInput
                    size={size}
                    label={shirtQuants[size].label}
                    quant={shirtQuants[size].quant}
                    changeFunc={changeQuant}
                    key={idx}
                />
            );
        }
    });

    const womenShirtInputDisplay = Object.keys(shirtQuants)?.map((size, idx)=> {
        if (shirtQuants[size].label.includes("Women")) {
            return (
                <ShirtInput
                    size={size}
                    label={shirtQuants[size].label}
                    quant={shirtQuants[size].quant}
                    changeFunc={changeQuant}
                    key={idx}
                />
            );
        }
    });

    const paypalDisplay = useMemo(()=> {
        return (
            <PayPalScriptProvider 
                options={{
                    clientId: eventConfig["payPalClientId"],
                    components: "buttons",
                    currency: "USD"
                }}
            >
                <PayPalButtons
                    style={style}
                    disabled={requestActive}
                    forceReRender={[style]}
                    fundingSource={undefined}
                    createOrder={(data, actions) => buyShirts(data, actions, totalCost)}
                    onApprove={(data, actions) => recordShirts(data, actions, shirtQuants)}
                />
            </PayPalScriptProvider>
        );
    }, [totalCost])

    try {
        return (
            <>
                <Card maxWidth="1400px" padding={isMobile() ? 3 : 8} mt={4} mb={4}>
                    <Heading size="md" mb={4}>Shirt Pre-order</Heading>
                    <Divider mb={4}/>                    
                    <Text mb={4}>
                        You can use the form below to pre-order a shirt to receive at the event.  It's <strong>${eventConfig["shirtsPrice"]}.00</strong> per shirt, and they'll be ordered on <strong>{eventConfig["shirtsCutoff"]}</strong>.
                        I'll only be shipping as many shirts as are pre-ordered, so make sure you fill this out before that cutoff date!
                    </Text>   
                    <Flex mb={4} flexDirection="column" flexFlow="wrap" gap={3} ml="auto" mr="auto" alignItems="center" justifyContent="space-evenly">                        
                        <Image display={isMobile() ? "block" : "none"} borderRadius={5} maxWidth={"230px"} src={process.env.PUBLIC_URL + '/images/sc2-shirts-preview.png'} />
                        <Flex maxWidth="230px" flexDirection="column" flexFlow="wrap" gap={3}>{menShirtInputDisplay}</Flex>
                        <Flex maxWidth="230px" flexDirection="column" flexFlow="wrap" gap={3}>{womenShirtInputDisplay}</Flex>
                        <Image display={isMobile() ? "none" : "block"} borderRadius={5} maxWidth={"500px"} src={process.env.PUBLIC_URL + '/images/sc2-shirts-preview.png'} />
                    </Flex>
                </Card>
                <Collapse in={totalCost} animateOpacity unmountOnExit>
                    <Card maxWidth="1400px" padding={isMobile() ? 3 : 8} mt={4} mb={4}>
                        <Heading align="center" size="xl" mb={4}>${totalCost}.00 Total</Heading>
                        <Flex mb={4} flexDirection="column" flexFlow="wrap" gap={3} alignItems="center" justifyContent="space-evenly">
                            <Box flex="1 100%" maxWidth={{base: "300px", xl: "650px"}} padding={4} ml="auto" mr="auto" borderRadius={8} bgColor="white">
                                {paypalDisplay}
                            </Box>
                        </Flex>
                    </Card>
                </Collapse>
            </>
        );
    } catch (e) {
        console.error("ShirtForm render error: ", e);
    }
}